import { useCallback, useEffect, useState } from 'react';

const GEOCODING_URL =
  'https://api.bigdatacloud.net/data/reverse-geocode-client';

const LOCATION_KEY = 'location';
const LOCATION_EXPIRATION_KEY = 'location_expiration';

const useLocation = () => {
  const [location, setLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadLocation = useCallback(() => {
    const expiration = localStorage.getItem(LOCATION_EXPIRATION_KEY);
    if (!expiration) {
      return null;
    }

    const now = new Date();
    const expirationTime = new Date(expiration);
    if (expirationTime < now) {
      return null;
    }

    const location = localStorage.getItem(LOCATION_KEY);
    if (!location) {
      return null;
    }

    return JSON.parse(location);
  }, []);

  const saveLocation = useCallback((location) => {
    const minutes = 1000 * 60;
    const now = new Date();
    const expirationTime = new Date(now.getTime() + 30 * minutes);

    localStorage.setItem(LOCATION_EXPIRATION_KEY, expirationTime.toString());
    localStorage.setItem(LOCATION_KEY, JSON.stringify(location));
  }, []);

  const fetchLocation = useCallback(async () => {
    setIsLoading(true);

    try {
      const locationResponse = await fetch(GEOCODING_URL);
      if (!locationResponse.ok) {
        throw new Error('Failed to fetch location');
      }

      const locationData = await locationResponse.json();
      const location = {
        country: locationData.countryName ?? '',
        city: locationData.locality ?? '',
      };

      setLocation(location);
      saveLocation(location);
    } catch (error) {
      console.error('Failed to fetch location', error);
    } finally {
      setIsLoading(false);
    }
  }, [saveLocation]);

  useEffect(() => {
    const location = loadLocation();

    if (location) {
      setLocation(location);
      setIsLoading(false);
    } else {
      fetchLocation();
    }
  }, [fetchLocation, loadLocation]);

  return { ...location, isLoading };
};

export default useLocation;
